import { useEffect, useReducer } from 'react';
import axios from 'axios';

const KEY_ACTIVE = 'adeeb-active-profile';
const EVENT = 'adeeb-profile-change';

// Reader endpoints that need the X-Profile-Id header. Anything not in this
// list (admin routes, public catalog, billing, etc.) is left alone.
const PROFILE_PATH_RE = /^\/api\/(progress|favorites|playlist|quizzes|rewards|notifications|discover)(\/|$)/;

let cache = readActive();

function readActive() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY_ACTIVE);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function emit() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function getActiveProfile() {
  return cache;
}

export function getActiveProfileId() {
  return cache?._id || null;
}

export function setActiveProfile(profile) {
  if (profile && profile._id) {
    cache = profile;
    localStorage.setItem(KEY_ACTIVE, JSON.stringify(profile));
  } else {
    cache = null;
    localStorage.removeItem(KEY_ACTIVE);
  }
  emit();
}

export function clearActiveProfile() { setActiveProfile(null); }

let interceptorRegistered = false;
/**
 * One-time axios interceptor — adds X-Profile-Id to every reader-scoped
 * request. Reacts to a server 400 PROFILE_REQUIRED by clearing the local
 * profile and bouncing the user to the picker.
 */
export function ensureProfileAxiosInterceptor() {
  if (interceptorRegistered || typeof window === 'undefined') return;
  axios.interceptors.request.use((config) => {
    const url = String(config.url || '');
    if (PROFILE_PATH_RE.test(url)) {
      const id = getActiveProfileId();
      if (id) {
        config.headers = config.headers || {};
        config.headers['X-Profile-Id'] = id;
      }
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const code = err?.response?.data?.code;
      const sentProfile = err?.config?.headers?.['X-Profile-Id'];
      const currentProfile = getActiveProfileId();
      // A request started before profile selection must not clear the new selection.
      if (String(sentProfile || '') !== String(currentProfile || '')) return Promise.reject(err);
      if (code === 'PROFILE_REQUIRED' || code === 'PROFILE_NOT_FOUND' || code === 'PROFILE_FORBIDDEN') {
        // Staff (teacher / school admin) have no reader profile — let
        // these errors fail quietly so they can browse the library
        // without being looped back to the teacher dashboard.
        let accountType = null;
        try { accountType = JSON.parse(localStorage.getItem('adeeb-user') || 'null')?.accountType; }
        catch { /* ignore */ }
        if (accountType !== 'teacher' && accountType !== 'school') {
          clearActiveProfile();
          window.dispatchEvent(new CustomEvent('adeeb-profile-needed'));
        }
      }
      return Promise.reject(err);
    }
  );
  interceptorRegistered = true;
}

// Reset the active profile whenever the auth changes (login / logout).
if (typeof window !== 'undefined') {
  window.addEventListener('adeeb-auth-change', () => {
    if (!localStorage.getItem('adeeb-token')) clearActiveProfile();
  });
}

export function useActiveProfile() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const onChange = () => force();
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return cache;
}
